import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ArrowUpRight, CheckCircle2, Loader2, ExternalLink, AlertTriangle, Users, Globe, ChevronLeft, Fingerprint } from "lucide-react";
import { DEFAULT_WALLET_NETWORK, getWalletNetwork } from "@/lib/networks";
import { KNOWN_CRYPTO_META } from "@/lib/crypto-data";
import { useWebAuthn } from "@/hooks/useWebAuthn";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'external' | 'internal';
  initialSymbol?: string;
  network?: string;
  onSuccess?: () => void;
}

type Step = 'input' | 'review' | 'done';

const trimSymbolList = (network: string) => getWalletNetwork(network).symbols;
const trimAmount = (value: number, decimals = 8) => {
  if (!Number.isFinite(value) || value <= 0) return '0';
  const factor = 10 ** decimals;
  const trimmed = Math.floor(value * factor) / factor;
  return trimmed.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '');
};

const Web3SendDialog = ({ open, onOpenChange, mode = 'external', initialSymbol, network = DEFAULT_WALLET_NETWORK, onSuccess }: Props) => {
  const { toast } = useToast();
  const { login: verifyPasskey, hasCredential } = useWebAuthn();

  const netCfg = getWalletNetwork(network);
  const allSymbolList = netCfg.symbols;
  const defaultSymbol = allSymbolList[0];
  const nativeSymbol = netCfg.native;

  const [step, setStep] = useState<Step>('input');
  const [symbol, setSymbol] = useState<string>(initialSymbol && allSymbolList.includes(initialSymbol.toUpperCase()) ? initialSymbol.toUpperCase() : defaultSymbol);

  // Keep the selected asset valid when the wallet network changes.
  useEffect(() => {
    if (!allSymbolList.includes(symbol)) setSymbol(defaultSymbol);
  }, [network]);
  const [toAddress, setToAddress] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [recipientFound, setRecipientFound] = useState(false);
  const [recipientName, setRecipientName] = useState<string | null>(null);
  const [recipientAvatar, setRecipientAvatar] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [amount, setAmount] = useState("");
  const [estimating, setEstimating] = useState(false);
  const [gasEth, setGasEth] = useState<number | null>(null);
  const [userEthBalance, setUserEthBalance] = useState<number | null>(null);
  const [needsAutoGas, setNeedsAutoGas] = useState(false);
  const [tokenForGas, setTokenForGas] = useState(0);
  const [liveGasEth, setLiveGasEth] = useState<number | null>(null);
  const [liveTokenForGas, setLiveTokenForGas] = useState<number>(0);
  const [liveNeedsAutoGas, setLiveNeedsAutoGas] = useState(false);
  const [liveEstimating, setLiveEstimating] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifyingPasskey, setVerifyingPasskey] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [loadingBalances, setLoadingBalances] = useState(false);

  // Only symbols the user actually holds (balance > 0)
  const symbolList = useMemo(
    () => allSymbolList.filter((s) => (balances[s] || 0) > 0),
    [balances, network],
  );
  const currentBalance = balances[symbol] || 0;

  useEffect(() => {
    if (open) {
      setStep('input');
      setToAddress(""); setRecipientId(""); setRecipientFound(false); setRecipientName(null); setRecipientAvatar(null);
      setAmount(""); setGasEth(null); setUserEthBalance(null); setTxHash(null);
      setNeedsAutoGas(false); setTokenForGas(0);
      // Fetch wallet balances so we can filter the dropdown and enable MAX.
      (async () => {
        setLoadingBalances(true);
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;
          const [{ data }, { data: reserved }] = await Promise.all([
            supabase.from('wallets').select('crypto_symbol, balance').eq('user_id', user.id).eq('network', netCfg.id).gt('balance', 0),
            supabase.from('platform_fee_accruals').select('fee_symbol, fee_amount').eq('user_id', user.id).eq('status', 'pending'),
          ]);
          const reservedMap: Record<string, number> = {};
          (reserved || []).forEach((r: any) => {
            const s = String(r.fee_symbol || '').toUpperCase();
            reservedMap[s] = (reservedMap[s] || 0) + Number(r.fee_amount || 0);
          });
          const map: Record<string, number> = {};
          (data || []).forEach((w: any) => {
            const sym = String(w.crypto_symbol || '').toUpperCase();
            if (allSymbolList.includes(sym)) {
              const available = Math.max(0, (Number(w.balance) || 0) - (reservedMap[sym] || 0));
              if (available > 0) map[sym] = available;
            }
          });
          setBalances(map);
          const initial = initialSymbol?.toUpperCase();
          const firstAvailable = Object.keys(map)[0];
          if (initial && map[initial]) setSymbol(initial);
          else if (firstAvailable) setSymbol(firstAvailable);
          else setSymbol(defaultSymbol);
        } finally {
          setLoadingBalances(false);
        }
      })();
    }
  }, [open, initialSymbol, network]);

  // Internal sends are ID-only. External sends are wallet-address-only.
  const rawRecipient = (mode === 'internal' ? recipientId : toAddress).trim();
  const isIdInput = /^\d{16}$/.test(rawRecipient);
  const isAddressInput = /^0x[a-fA-F0-9]{40}$/.test(rawRecipient);
  const lookupKey = mode === 'internal' && isIdInput ? rawRecipient : '';

  useEffect(() => {
    if (!lookupKey) {
      setRecipientFound(false); setRecipientName(null); setRecipientAvatar(null); return;
    }
    let cancel = false;
    (async () => {
      setLookingUp(true);
      try {
        const { data, error } = await supabase.functions.invoke('tatum-eth-send', {
          body: { action: 'lookup-internal', recipientId: lookupKey, network },
        });
        if (cancel) return;
        if (error || data?.error) {
          setRecipientFound(false); setRecipientName(null); setRecipientAvatar(null);
          if (data?.error) toast({ title: 'تنبيه', description: data.error, variant: 'destructive' });
        } else if (data?.found) {
          setRecipientFound(true);
          setRecipientName(data.name || null);
          setRecipientAvatar(data.avatar_url || null);
        } else {
          setRecipientFound(false); setRecipientName(null); setRecipientAvatar(null);
        }
      } finally {
        if (!cancel) setLookingUp(false);
      }
    })();
    return () => { cancel = true; };
  }, [lookupKey, toast]);

  const finalAddress = mode === 'external' && isAddressInput ? rawRecipient : null;
  const canContinue = mode === 'internal' ? (isIdInput && recipientFound) : !!finalAddress;


  const meta = KNOWN_CRYPTO_META[symbol];

  // Live gas estimation whenever the coin (or dialog) changes, so the user
  // sees fees + total under the amount input before hitting "متابعة".
  useEffect(() => {
    if (!open || step !== 'input' || !symbol) return;
    if (mode === 'internal') {
      setLiveGasEth(0); setLiveTokenForGas(0); setLiveNeedsAutoGas(false); setLiveEstimating(false);
      return;
    }
    let cancel = false;
    setLiveEstimating(true);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('tatum-eth-send', {
          body: { action: 'estimate-gas', cryptoSymbol: symbol, mode, network },
        });
        if (cancel) return;
        if (error || data?.error) {
          setLiveGasEth(null); setLiveTokenForGas(0); setLiveNeedsAutoGas(false);
        } else {
          setLiveGasEth(Number(data.gasEth || 0));
          setLiveTokenForGas(Number(data.tokenForGas || 0));
          setLiveNeedsAutoGas(!!data.needsAutoGas);
          // Do not overwrite the displayed balance with the on-chain balance —
          // the wallet ledger balance is the source of truth for the UI.
        }
      } finally {
        if (!cancel) setLiveEstimating(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, step, symbol, mode, network]);

  const amountNum = parseFloat(amount || '0') || 0;
  const feeInSymbol = mode === 'internal' ? 0 : symbol === nativeSymbol
    ? (liveGasEth ?? 0)
    : (liveNeedsAutoGas || mode === 'external' ? liveTokenForGas : 0);
  const totalInSymbol = amountNum + feeInSymbol;



  const handleContinue = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'خطأ', description: 'أدخل مبلغ صالح', variant: 'destructive' }); return;
    }
    if (mode === 'internal') {
      if (!isIdInput || !recipientFound) {
        toast({ title: 'خطأ', description: 'أدخل معرّف مستلم صحيح مكوّن من 16 رقم', variant: 'destructive' });
        return;
      }
      setGasEth(0);
      setUserEthBalance(null);
      setNeedsAutoGas(false);
      setTokenForGas(0);
      setStep('review');
      return;
    }
    if (!finalAddress || !/^0x[a-fA-F0-9]{40}$/.test(finalAddress)) {
      toast({ title: 'خطأ', description: 'عنوان المستلم غير صالح', variant: 'destructive' });
      return;
    }
    setEstimating(true);
    try {
      const { data, error } = await supabase.functions.invoke('tatum-eth-send', {
        body: { action: 'estimate-gas', cryptoSymbol: symbol, mode, network },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message || 'فشل حساب رسوم الشبكة');
      const maxSendable = Number(data.maxSendableAmount ?? currentBalance);
      if (Number.isFinite(maxSendable) && amountNum > maxSendable + 1e-10) {
        setAmount(trimAmount(maxSendable));
        toast({
          title: 'المبلغ أكبر من الحد المتاح',
          description: `بعد خصم عمولة الإرسال، الحد الأقصى هو ${trimAmount(maxSendable)} ${symbol}`,
          variant: 'destructive',
        });
        return;
      }
      setGasEth(data.gasEth);
      setUserEthBalance(data.userEthBalance);
      setNeedsAutoGas(!!data.needsAutoGas);
      setTokenForGas(Number(data.tokenForGas || 0));
      setStep('review');
    } catch (e: any) {
      const description = e?.message?.includes('Edge Function') ? 'تعذر حساب الرسوم حالياً. حاول مرة أخرى بعد قليل.' : e.message;
      toast({ title: 'فشل تقدير الرسوم', description, variant: 'destructive' });
    } finally {
      setEstimating(false);
    }
  };

  const handleMax = async () => {
    if (currentBalance <= 0) return;
    if (mode === 'internal') {
      setAmount(currentBalance.toFixed(8).replace(/0+$/, '').replace(/\.$/, ''));
      return;
    }
    setEstimating(true);
    try {
      const { data, error } = await supabase.functions.invoke('tatum-eth-send', {
        body: { action: 'estimate-gas', cryptoSymbol: symbol, mode, network },
      });
      if (error || data?.error) throw new Error(data?.error || 'فشل حساب الرسوم');
      const max = Number(data.maxSendableAmount ?? 0);
      // Keep the displayed balance as the ledger balance; do not clamp to on-chain.
      setLiveGasEth(Number(data.gasEth || 0));
      setLiveTokenForGas(Number(data.tokenForGas || 0));
      setLiveNeedsAutoGas(!!data.needsAutoGas);
      setAmount(trimAmount(max));
    } catch (e: any) {
      toast({ title: 'تعذر حساب الحد الأقصى', description: e.message, variant: 'destructive' });
    } finally {
      setEstimating(false);
    }
  };

  const handleConfirmAndSend = async () => {
    // Passkey verification at the FINAL step only
    if (!hasCredential) {
      toast({ title: 'مفتاح Passkey غير مسجّل', description: 'يرجى تسجيل مفتاح Passkey من صفحة الملف الشخصي أولاً.', variant: 'destructive' });
      return;
    }
    setVerifyingPasskey(true);
    const auth = await verifyPasskey();
    setVerifyingPasskey(false);
    if (!auth?.success) {
      toast({ title: 'فشل التحقق', description: auth?.error || 'تم إلغاء التحقق من Passkey.', variant: 'destructive' });
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('tatum-eth-send', {
        body: {
          action: 'send',
          cryptoSymbol: symbol,
          toAddress: mode === 'external' ? finalAddress : undefined,
          recipientId: mode === 'internal' ? rawRecipient : undefined,
          recipientInputType: mode === 'internal' ? 'id' : 'address',
          amount: parseFloat(amount),
          mode,
          network,
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message || 'فشل الإرسال');
      setTxHash(data.txHash || '');
      setStep('done');
      onSuccess?.();
    } catch (e: any) {
      const description = e?.message?.includes('Edge Function') ? 'تعذر تنفيذ الإرسال حالياً. تحقق من الرصيد ورسوم الغاز ثم حاول مرة أخرى.' : e.message;
      toast({ title: 'فشل الإرسال', description, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const isPending = estimating || sending || verifyingPasskey;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'internal' ? <Users className="w-5 h-5 text-primary" /> : <Globe className="w-5 h-5 text-primary" />}
            {mode === 'internal' ? 'إرسال داخلي عبر ID' : 'إرسال خارجي على البلوكتشين'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            شبكة {netCfg.id} فقط — إرسال مباشر دون أي وسيط
          </DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-4">


            <div className="space-y-2">
              <Label>العملة</Label>
              {loadingBalances ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> جاري تحميل الأرصدة...</p>
              ) : symbolList.length === 0 ? (
                <p className="text-xs text-destructive">لا يوجد لديك أي عملة برصيد قابل للإرسال.</p>
              ) : (
                <Select value={symbol} onValueChange={setSymbol}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {symbolList.map((s) => {
                      const m = KNOWN_CRYPTO_META[s];
                      return (
                        <SelectItem key={s} value={s}>
                          <span className="flex items-center gap-2">
                            <span>{m?.icon}</span>
                            <span>{s}</span>
                            <span className="text-muted-foreground text-xs">— {(balances[s] || 0).toFixed(6)}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            {mode === 'internal' ? (
              <div className="space-y-2">
                <Label>معرّف المستلم (ID)</Label>
                <Input
                  value={recipientId}
                  onChange={(e) => setRecipientId(e.target.value.replace(/\D/g, '').slice(0, 16))}
                  placeholder="16 رقم"
                  dir="ltr"
                  inputMode="numeric"
                  className="font-mono text-sm"
                />
                <p className="text-[10px] text-muted-foreground">
                  أدخل معرّف المستخدم المكوّن من 16 رقم فقط.
                </p>
                {isIdInput && lookingUp && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> جاري البحث عن المستلم...</p>
                )}
                {isIdInput && recipientFound && recipientName && (
                  <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-2 text-xs flex items-start gap-2">
                    <Avatar className="w-9 h-9 border border-green-500/30">
                      <AvatarImage src={recipientAvatar || undefined} alt={recipientName} />
                      <AvatarFallback className="text-xs bg-primary/15 text-primary">{recipientName.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-green-500">✓ {recipientName}</p>
                      <p className="font-mono text-muted-foreground break-all" dir="ltr">ID: {recipientId}</p>
                    </div>
                  </div>
                )}
                {isIdInput && !lookingUp && !recipientFound && (
                  <p className="text-xs text-destructive">المعرّف غير موجود.</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>عنوان المحفظة</Label>
                <Input
                  value={toAddress}
                  onChange={(e) => setToAddress(e.target.value)}
                  placeholder="0x..."
                  dir="ltr"
                  className="font-mono text-sm"
                />
                <p className="text-[10px] text-muted-foreground">
                  أدخل عنوان محفظة {netCfg.id} (0x...) فقط.
                </p>
                {isAddressInput && (
                  <p className="text-xs text-green-500">✓ عنوان محفظة صالح</p>
                )}
                {rawRecipient && !isAddressInput && (
                  <p className="text-xs text-destructive">الإرسال الخارجي يقبل عنوان محفظة فقط.</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>المبلغ ({symbol})</Label>
                <span className="text-xs text-muted-foreground">
                  الرصيد: <span className="font-mono">{currentBalance.toFixed(6)}</span>
                </span>
              </div>
              <div className="flex gap-2">
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.0" dir="ltr" min="0" step="any" className="flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={handleMax} disabled={estimating || currentBalance <= 0} className="shrink-0">
                  {estimating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'الحد الأقصى'}
                </Button>
              </div>

              <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">عمولة الإرسال</span>
                  <span className="font-mono">
                    {mode === 'internal'
                      ? `0.000000 ${symbol}`
                      : liveEstimating && liveGasEth == null
                      ? <Loader2 className="w-3 h-3 animate-spin inline" />
                      : `${feeInSymbol.toFixed(6)} ${symbol}`}
                  </span>
                </div>
                <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
                  <span>الإجمالي بعد خصم العمولة</span>
                  <span className="font-mono text-primary">{totalInSymbol.toFixed(6)} {symbol}</span>
                </div>
                <p className="text-[10px] text-muted-foreground pt-1">
                  سيصل للمستلم <span className="font-mono">{amountNum.toFixed(6)} {symbol}</span>{mode === 'internal' ? ' — تحويل داخلي بدون رسوم.' : ' — زر الحد الأقصى يخصم عمولة الإرسال فقط من الرصيد.'}
                </p>
              </div>
            </div>


            <Button onClick={handleContinue} disabled={isPending || !amount || !canContinue} className="w-full">
              {estimating ? <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري حساب الرسوم...</> : 'متابعة'}
            </Button>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-secondary/40 p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">العملة</span>
                <span className="font-semibold">{meta?.icon} {symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">المبلغ</span>
                <span className="font-mono font-semibold">{amount} {symbol}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground shrink-0">إلى</span>
                <span className="font-mono text-xs break-all text-left" dir="ltr">{mode === 'internal' ? recipientId : finalAddress}</span>
              </div>
              {mode === 'internal' && recipientName && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">المستلم</span>
                  <span className="flex items-center gap-2 min-w-0">
                    <Avatar className="w-7 h-7 border border-border">
                      <AvatarImage src={recipientAvatar || undefined} alt={recipientName} />
                      <AvatarFallback className="text-[10px] bg-primary/15 text-primary">{recipientName.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="truncate">{recipientName}</span>
                  </span>
                </div>
              )}
              {mode === 'external' ? (
                <>
                  <div className="border-t border-border pt-3 flex justify-between">
                    <span className="text-muted-foreground">عمولة الإرسال الفعلية</span>
                    <span className="font-mono">{gasEth?.toFixed(6)} {nativeSymbol}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">رصيد {nativeSymbol} (للغاز)</span>
                    <span className={`font-mono ${userEthBalance != null && gasEth != null && userEthBalance < gasEth && !needsAutoGas ? 'text-destructive' : 'text-green-500'}`}>
                      {userEthBalance?.toFixed(6)} {nativeSymbol}
                    </span>
                  </div>
                </>
              ) : (
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="text-muted-foreground">الرسوم</span>
                  <span className="font-mono text-green-500">0 {symbol}</span>
                </div>
              )}
              {needsAutoGas && tokenForGas > 0 && (
                <div className="border-t border-border pt-3 flex justify-between text-xs">
                  <span className="text-muted-foreground">رسوم إضافية بـ {symbol}</span>
                  <span className="font-mono text-yellow-500">+ {tokenForGas.toFixed(6)} {symbol}</span>
                </div>
              )}
              <div className="border-t border-border pt-3 flex justify-between font-semibold">
                <span>سيصل للمستلم</span>
                <span className="font-mono text-green-500">
                  {parseFloat(amount || '0').toFixed(6)} {symbol}
                </span>
              </div>
            </div>

            {needsAutoGas && tokenForGas > 0 && (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs space-y-1">
                <p className="font-semibold">⚡ رسوم الشبكة مضافة فوق المبلغ</p>
                <p className="text-muted-foreground">
                  سيصل للمستلم كامل المبلغ <span className="font-mono font-semibold">{parseFloat(amount || '0').toFixed(6)} {symbol}</span>،
                  وسيتم خصم رسوم الشبكة (~<span className="font-mono">{tokenForGas.toFixed(6)} {symbol}</span>) إضافةً من رصيدك.
                </p>
              </div>
            )}



            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 flex gap-2 text-xs">
              <Fingerprint className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <p>سيُطلب منك تأكيد العملية بـ <strong>Passkey</strong> عند الضغط على زر التأكيد.</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('input')} disabled={isPending} className="flex-1">
                <ChevronLeft className="w-4 h-4 ml-1" /> رجوع
              </Button>
              <Button onClick={handleConfirmAndSend} disabled={isPending} className="flex-1">
                {verifyingPasskey ? <><Fingerprint className="w-4 h-4 animate-pulse ml-2" /> Passkey...</>
                  : sending ? <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري الإرسال...</>
                  : 'تأكيد الإرسال'}
              </Button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-sm font-semibold">تم إرسال المعاملة بنجاح!</p>
              {txHash && (
                <p className="text-xs text-muted-foreground font-mono break-all px-4" dir="ltr">{txHash}</p>
              )}
            </div>
            {txHash && (
              <a href={`${netCfg.explorerTx}${txHash}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-primary hover:underline">
                <ExternalLink className="w-4 h-4" /> عرض على {netCfg.id === 'Ethereum (ERC20)' ? 'Etherscan' : 'PolygonScan'}
              </a>
            )}
            <Button onClick={() => onOpenChange(false)} className="w-full">إغلاق</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Web3SendDialog;
